import { LayoutGroup, motion } from 'framer-motion';
import React from 'react';
import styles from './ContentTypeMenu.module.scss';

export interface ContentTypeMenuItem {
	id: string;
	title: string;
	/** Revealed under the title of the active item; desktop only */
	content?: React.ReactNode;
}

interface Props {
	items: ContentTypeMenuItem[];
	activeId: string;
	onSelect: (id: string) => void;
	/** Prefix for the ids that tie each title to its content */
	idPrefix: string;
	className?: string;
}

// One spring for the indicator and the content, so they land together
const TRANSITION = { type: 'spring', stiffness: 380, damping: 36 } as const;

// `visibility` takes a closed item's links out of the tab order and the
// accessibility tree while they stay in the HTML
const contentVariants = {
	open: {
		height: 'auto',
		opacity: 1,
		visibility: 'visible' as const,
		transition: TRANSITION,
	},
	closed: {
		height: 0,
		opacity: 0,
		transition: TRANSITION,
		transitionEnd: { visibility: 'hidden' as const },
	},
};

/**
 * Image / Video / Audio switcher: a vertical list whose active item opens up
 * on desktop, a row of tabs on phones. The marker slides between items rather
 * than jumping. Every item's content stays in the markup (collapsed), so all of
 * it is indexable, not just the open one.
 */
export const ContentTypeMenu: React.FC<Props> = ({
	items,
	activeId,
	onSelect,
	idPrefix,
	className,
}) => (
	<LayoutGroup id={idPrefix}>
		<ul className={`${styles['menu']}${className ? ` ${className}` : ''}`}>
			{items.map(item => {
				const isActive = item.id === activeId;
				const contentId = `${idPrefix}-${item.id}`;

				return (
					<li
						key={item.id}
						className={`${styles['menu__item']}${
							isActive ? ` ${styles['menu__item_active']}` : ''
						}`}
					>
						<button
							type='button'
							className={styles['menu__title']}
							aria-expanded={isActive}
							aria-controls={item.content ? contentId : undefined}
							onClick={() => onSelect(item.id)}
						>
							{item.title}
						</button>

						{item.content && (
							<div className={styles['menu__content-wrap']}>
								<motion.div
									id={contentId}
									className={styles['menu__content']}
									variants={contentVariants}
									initial={false}
									animate={isActive ? 'open' : 'closed'}
								>
									<div className={styles['menu__content-inner']}>
										{item.content}
									</div>
								</motion.div>
							</div>
						)}

						{isActive && (
							<motion.span
								layoutId='indicator'
								className={styles['menu__indicator']}
								transition={TRANSITION}
								aria-hidden='true'
							/>
						)}
					</li>
				);
			})}
		</ul>
	</LayoutGroup>
);
